import * as THREE from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { PanoramaSource } from "./PanoramaSource.ts";

const PANO_DEFAULT_FOV = 75;

interface MeshMatState {
  mat: THREE.Material;
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
}
interface MeshState {
  mesh: THREE.Mesh;
  materials: MeshMatState[];
}
const DRAG_SENSITIVITY = 0.003;
const PITCH_LIMIT = Math.PI * 0.48;
const CAMERA_HEIGHT = 1.55;  // standing camera height above floor (m)

/**
 * Reusable panorama session: BackSide sphere around the camera, drag-to-look,
 * neighbour arrows, WASD/wheel to step. Texture provisioning is delegated to
 * a {@link PanoramaSource}.
 *
 * Visual model: the sphere is a directional skybox parented to the renderer's
 * camera position. World position of the sphere doesn't affect sampling for
 * either the pinhole-blend shader or equirect mapping — sample direction is
 * the only input. So we keep the sphere at the camera each render frame.
 */
export class PanoramaSession {
  private sphere: THREE.Mesh | null = null;
  private active_ = false;
  private loading = false;

  private savedBackground: THREE.Color | THREE.Texture | THREE.CubeTexture | null = null;
  private savedCamPos = new THREE.Vector3();
  private savedCamQuat = new THREE.Quaternion();
  private savedCamFov = 45;
  private savedControlsTarget = new THREE.Vector3();

  private currentNodeId = -1;
  private yaw = 0;
  private pitch = 0;
  private fov = PANO_DEFAULT_FOV;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private downX = 0;
  private downY = 0;
  private dragMoved = false;

  private hotspotGroup: THREE.Group | null = null;
  private hoveredHotspot: THREE.Object3D | null = null;

  private ghostSphere: THREE.Mesh | null = null;
  private transitionMaterial: THREE.MeshBasicMaterial | null = null;
  private transitionRaf = 0;
  private static readonly TRANSITION_MS = 380;
  private static readonly MESH_FLIGHT_MS = 800;
  private static readonly MESH_FADE_MS = 220;
  private static readonly PRELANDING_MS = 200;

  private savedMeshStates: MeshState[] = [];
  private meshFlightResolve: (() => void) | null = null;

  private meshBlendOpacity = 0;
  private blendStates: MeshState[] = [];

  private overlay: HTMLElement;
  private nav: HTMLElement;
  private info: HTMLElement;
  private loadingDiv: HTMLElement;

  /** Fired after a successful exit (panorama closed, scene restored). */
  onExit: (() => void) | null = null;
  /** Fired after a successful navigate (texture loaded, ready to render). */
  onNodeChange: ((nodeId: number) => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private controls: OrbitControls,
    private planRoot: THREE.Group,
    private source: PanoramaSource,
    overlayEl: HTMLElement | string = "pano-overlay",
  ) {
    this.overlay = typeof overlayEl === "string"
      ? document.getElementById(overlayEl)!
      : overlayEl;
    this.info = this.overlay.querySelector(".pano-info") as HTMLElement;
    this.nav = this.overlay.querySelector(".pano-nav") as HTMLElement;
    this.loadingDiv = this.overlay.querySelector(".pano-loading") as HTMLElement;

    const c = this.renderer.domElement;
    c.addEventListener("pointerdown", this._down);
    c.addEventListener("pointermove", this._move);
    c.addEventListener("pointerup", this._up);
    c.addEventListener("pointerleave", this._up);
    c.addEventListener("wheel", this._wheel, { passive: false });
  }

  get isActive(): boolean { return this.active_; }
  get nodeId(): number { return this.currentNodeId; }

  /** Find the closest panorama node to the current orbit camera. */
  findNearestToCamera(): number {
    let bestId = -1, bestD2 = Infinity;
    for (const n of this.source.getNodes()) {
      const d2 = n.position.distanceToSquared(this.camera.position);
      if (d2 < bestD2) { bestD2 = d2; bestId = n.id; }
    }
    return bestId;
  }

  /** Enter panorama mode at the given node. Idempotent. */
  async enter(nodeId: number): Promise<void> {
    if (this.active_ || this.loading) return;
    if (!this.source.getNodes().some(n => n.id === nodeId)) return;

    this.active_ = true;
    this._ensureSphere();

    this.savedBackground = this.scene.background as THREE.Color | THREE.Texture | THREE.CubeTexture | null;
    this.savedCamPos.copy(this.camera.position);
    this.savedCamQuat.copy(this.camera.quaternion);
    this.savedCamFov = this.camera.fov;
    this.savedControlsTarget.copy(this.controls.target);

    if (document.pointerLockElement) document.exitPointerLock();
    this.controls.enabled = false;
    this.controls.disconnect();
    this.planRoot.visible = false;
    this.scene.background = new THREE.Color(0x050505);

    // Move the camera to the node's world position so position-aware overlays
    // (mesh wireframe, hotspots) render from the photo's true POV. The skybox
    // sphere itself is rotation-only — but it MUST stay centered on the camera
    // for equirect texture coords to remain undistorted, so we co-locate them.
    const nodePos = this.source.getNodes().find(n => n.id === nodeId)?.position;
    if (nodePos) this.camera.position.copy(nodePos);

    this.sphere!.position.copy(this.camera.position);
    this.scene.add(this.sphere!);

    this._ensureHotspotGroup();
    this.scene.add(this.hotspotGroup!);

    this.fov = PANO_DEFAULT_FOV;
    this._setNodeOrientation(nodeId);
    this._applyCamera();

    this.overlay.classList.add("open");
    this.renderer.domElement.style.cursor = "grab";

    document.addEventListener("keydown", this._key);

    await this.navigate(nodeId);
  }

  /** Leave panorama mode and restore the previous camera/controls state. */
  exit(): void {
    if (!this.active_) return;
    this.active_ = false;
    this.currentNodeId = -1;

    if (this.meshBlendOpacity > 0) this._clearBlend();
    this._cancelTransition();
    this.sphere?.removeFromParent();
    this._setHotspotHover(null);
    this._disposeHotspotChildren();
    this.hotspotGroup?.removeFromParent();
    this.scene.background = this.savedBackground;
    this.planRoot.visible = true;
    this.controls.connect(this.renderer.domElement);  // three r0.180: connect() requires the element
    this.controls.enabled = true;
    this.camera.position.copy(this.savedCamPos);
    this.camera.quaternion.copy(this.savedCamQuat);
    this.camera.fov = this.savedCamFov;
    this.camera.updateProjectionMatrix();
    this.controls.target.copy(this.savedControlsTarget);

    this.overlay.classList.remove("open");
    this.renderer.domElement.style.cursor = "";
    document.removeEventListener("keydown", this._key);

    this.onExit?.();
  }

  /** Switch to another node within an active session. */
  async navigate(nodeId: number): Promise<void> {
    if (!this.active_ || this.loading) return;
    if (!this.source.getNodes().some(n => n.id === nodeId)) return;
    if (nodeId === this.currentNodeId) return;

    const isFirstNavigate = this.currentNodeId < 0;
    const fromPos = this.camera.position.clone();
    const toPos = this.source.getNodes().find(n => n.id === nodeId)?.position;
    if (!toPos) return;

    this.currentNodeId = nodeId;
    this.loading = true;
    this.loadingDiv.style.display = "";

    if (isFirstNavigate) {
      // First load: teleport immediately, no transition.
      this.camera.position.copy(toPos);
      if (this.sphere) this.sphere.position.copy(toPos);
      this._setNodeOrientation(nodeId);
      this._applyCamera();

      await this.source.loadNode(nodeId);
      if (!this.active_) { this.loading = false; return; }

      if (this.sphere) this.sphere.rotation.y = this.source.getSphereYaw(nodeId);
      this.loading = false;
      this.loadingDiv.style.display = "none";
      this._updateHotspots(nodeId);
      this._renderNav(nodeId);
      this.info.textContent = `Node ${nodeId} / ${this.source.getNodes().length}`;
      this.onNodeChange?.(nodeId);
      // No ghost exists here, so _startTransition just calls onComplete immediately.
      this._startTransition(() => this.source.preloadNeighbors(nodeId));
      return;
    }

    // Subsequent navigation: 3-phase 3D mesh transition.
    // If blend is active, temporarily restore opaque materials so the flight
    // animation always starts from a clean state.
    const hadBlend = this.meshBlendOpacity > 0;
    if (hadBlend) this._clearBlend();

    this._snapshotGhost();
    if (this.hotspotGroup) this.hotspotGroup.visible = false;

    // Load texture concurrently with the flight animation.
    const texturePromise = this.source.loadNode(nodeId);
    await this._runMeshFlight(fromPos, toPos, texturePromise, nodeId);

    if (!this.active_) { this.loading = false; return; }

    // Reapply blend after the flight if it was active before navigation.
    if (hadBlend && this.meshBlendOpacity > 0) {
      this.blendStates = this._saveMeshStates();
      this.planRoot.visible = true;
      this._applyBlend(this.meshBlendOpacity);
    }

    // Anchor sphere at destination (flight may have landed within float epsilon).
    this.camera.position.copy(toPos);
    if (this.sphere) {
      this.sphere.position.copy(toPos);
      this.sphere.rotation.y = this.source.getSphereYaw(nodeId);
    }

    this.loading = false;
    this.loadingDiv.style.display = "none";
    if (this.hotspotGroup) this.hotspotGroup.visible = true;
    this._updateHotspots(nodeId);
    this._renderNav(nodeId);
    this.info.textContent = `Node ${nodeId} / ${this.source.getNodes().length}`;
    this.onNodeChange?.(nodeId);
    this.source.preloadNeighbors(nodeId);
  }

  /** Step in 2D direction (XZ); picks the most aligned neighbour. */
  navigateInDirection(dx: number, dz: number): void {
    if (this.loading || !this.active_) return;
    const neighbors = this.source.getNeighbors(this.currentNodeId);
    if (!neighbors.length) return;
    const cp = this._pos(this.currentNodeId);
    if (!cp) return;
    const td = new THREE.Vector2(dx, dz).normalize();
    let best = -1, bestDot = -Infinity;
    for (const nid of neighbors) {
      const np = this._pos(nid);
      if (!np) continue;
      const dot = td.dot(new THREE.Vector2(np.x - cp.x, np.z - cp.z).normalize());
      if (dot > bestDot) { bestDot = dot; best = nid; }
    }
    if (best >= 0 && bestDot > 0) this.navigate(best);
  }

  /** Live-update the sphere's Y rotation (used by the alignment UI's yaw slider). */
  setLiveSphereYaw(yaw: number): void {
    if (this.active_ && this.sphere) this.sphere.rotation.y = yaw;
  }

  /**
   * Blend the 3D mesh on top of the panorama sphere.
   * opacity=0 → pure panorama; opacity=1 → opaque mesh over panorama.
   * Ignored while a navigation transition is in progress.
   */
  setMeshBlend(opacity: number): void {
    if (!this.active_ || this.loading) return;
    opacity = Math.max(0, Math.min(1, opacity));
    const prev = this.meshBlendOpacity;
    this.meshBlendOpacity = opacity;

    if (opacity === 0) {
      if (prev > 0) this._clearBlend();
      return;
    }

    if (prev === 0) {
      // First activation: snapshot original material states and show the mesh.
      this.blendStates = this._saveMeshStates();
      this.planRoot.visible = true;
    }
    this._applyBlend(opacity);
  }

  dispose(): void {
    this.exit();
    this._cancelTransition();
    if (this.sphere) {
      this.sphere.geometry.dispose();
      this.sphere = null;
    }
    this._disposeHotspotChildren();
    this.hotspotGroup = null;
    const c = this.renderer.domElement;
    c.removeEventListener("pointerdown", this._down);
    c.removeEventListener("pointermove", this._move);
    c.removeEventListener("pointerup", this._up);
    c.removeEventListener("pointerleave", this._up);
    c.removeEventListener("wheel", this._wheel);
    document.removeEventListener("keydown", this._key);
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private _ensureSphere(): void {
    if (this.sphere) return;
    this.sphere = new THREE.Mesh(
      new THREE.SphereGeometry(50, 64, 32),
      this.source.getMaterial(),
    );
    this.sphere.renderOrder = -1;
  }

  private _setNodeOrientation(nodeId: number): void {
    const node = this.source.getNodes().find(n => n.id === nodeId);
    if (!node) return;
    this.yaw = node.initialYaw;
    this.pitch = node.initialPitch;
  }

  private _pos(id: number): THREE.Vector3 | null {
    return this.source.getNodes().find(n => n.id === id)?.position ?? null;
  }

  private _applyCamera(): void {
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch));
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.set(this.pitch, this.yaw, 0);
    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();
  }

  private _renderNav(nodeId: number): void {
    this.nav.innerHTML = "";
    const cp = this._pos(nodeId);
    if (!cp) return;
    const sorted = this.source.getNeighbors(nodeId)
      .map(nid => {
        const np = this._pos(nid);
        return np ? { id: nid, angle: Math.atan2(np.x - cp.x, np.z - cp.z) } : null;
      })
      .filter((x): x is { id: number; angle: number } => x !== null)
      .sort((a, b) => a.angle - b.angle);

    for (const { id: nid, angle } of sorted) {
      const btn = document.createElement("button");
      btn.className = "pano-nav-btn";
      const deg = THREE.MathUtils.radToDeg(-angle) + 180;
      btn.innerHTML =
        `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
        `stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" ` +
        `style="transform:rotate(${deg}deg)">` +
        `<line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;
      btn.addEventListener("click", () => { void this.navigate(nid); });
      this.nav.appendChild(btn);
    }
  }

  // ── Crossfade transition between nodes ────────────────────────────────────

  /** Clone the current material + spawn a ghost sphere matching the live one. */
  private _snapshotGhost(): void {
    this._cancelTransition();
    if (!this.sphere) return;
    const baseMat = this.source.getMaterial() as THREE.MeshBasicMaterial;
    if (!baseMat || typeof (baseMat as THREE.MeshBasicMaterial).clone !== "function") return;

    const clone = baseMat.clone();
    clone.transparent = true;
    clone.opacity = 1.0;
    clone.depthTest = false;
    clone.depthWrite = false;
    this.transitionMaterial = clone;

    const ghost = new THREE.Mesh(this.sphere.geometry, clone);
    ghost.position.copy(this.sphere.position);
    ghost.rotation.copy(this.sphere.rotation);
    ghost.scale.copy(this.sphere.scale);
    // Sphere renderOrder=-1, mesh renderOrder=0, ghost renderOrder=2 so ghost
    // draws above the mesh during the 3D transition (hotspots are at 100).
    ghost.renderOrder = 2;
    this.ghostSphere = ghost;
    this.scene.add(ghost);
  }

  private _startTransition(onComplete: () => void): void {
    if (!this.ghostSphere || !this.transitionMaterial) {
      onComplete();
      return;
    }
    const mat = this.transitionMaterial;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / PanoramaSession.TRANSITION_MS);
      // Ease-out cubic — most of the fade is up front so the new image reads early.
      mat.opacity = (1 - t) ** 3;
      if (t < 1 && this.active_) {
        this.transitionRaf = requestAnimationFrame(tick);
      } else {
        this._cancelTransition();
        onComplete();
      }
    };
    this.transitionRaf = requestAnimationFrame(tick);
  }

  // ── Mesh-fade 3D transition ────────────────────────────────────────────────

  /**
   * Three-phase Matterport-style transition:
   *   Phase 1 (0 – MESH_FADE_MS):   ghost fades out, mesh fades in.
   *   Phase 2 (FADE_MS – FLIGHT_MS): camera flies along a bezier arc,
   *                                   mesh fully opaque, ghost gone.
   *   Phase 3 (after flight + texture): mesh fades out, new panorama reveals.
   *
   * Resolves only after all phases complete (or when _cancelTransition fires).
   */
  private _runMeshFlight(
    fromPos: THREE.Vector3,
    toPos: THREE.Vector3,
    textureReady: Promise<void>,
    destNodeId: number,
  ): Promise<void> {
    return new Promise<void>(resolve => {
      this.meshFlightResolve = resolve;

      const FLIGHT = PanoramaSession.MESH_FLIGHT_MS;
      const FADE  = PanoramaSession.MESH_FADE_MS;

      this.savedMeshStates = this._saveMeshStates();
      this.planRoot.visible = true;
      this._setMeshOpacity(0);

      let textureLoaded = false;
      textureReady.then(() => { textureLoaded = true; });

      // Bezier arc: gentle rise at midpoint so the "fly-through" feels spatial.
      const arc = fromPos.clone().lerp(toPos, 0.5);
      arc.y += Math.min(1.0, fromPos.distanceTo(toPos) * 0.15);

      let flightDone = false;
      let fadeOutStart = -1;
      const t0 = performance.now();

      const tick = (now: number): void => {
        const elapsed = now - t0;

        // ── Camera flight along quadratic bezier ──────────────────────────
        if (!flightDone) {
          const raw = Math.min(1, elapsed / FLIGHT);
          // ease-in-out cubic
          const e = raw < 0.5 ? 4 * raw ** 3 : 1 - (-2 * raw + 2) ** 3 / 2;
          const me = 1 - e;
          this.camera.position.set(
            me * me * fromPos.x + 2 * me * e * arc.x + e * e * toPos.x,
            me * me * fromPos.y + 2 * me * e * arc.y + e * e * toPos.y,
            me * me * fromPos.z + 2 * me * e * arc.z + e * e * toPos.z,
          );
          if (raw >= 1) flightDone = true;
        }

        // Equirect spheres must stay centred on the camera every frame.
        if (this.sphere) this.sphere.position.copy(this.camera.position);
        if (this.ghostSphere) this.ghostSphere.position.copy(this.camera.position);

        // ── Ghost fade-out (first 75 % of flight) ─────────────────────────
        if (this.transitionMaterial) {
          const gt = Math.min(1, elapsed / (FLIGHT * 0.75));
          this.transitionMaterial.opacity = (1 - gt) ** 2;
        }

        // ── Mesh opacity ───────────────────────────────────────────────────
        const PRELANDING = PanoramaSession.PRELANDING_MS;
        const fadeOutReady = textureLoaded && (elapsed >= FLIGHT - PRELANDING || flightDone);
        let meshOp: number;
        if (elapsed < FADE) {
          meshOp = elapsed / FADE;                      // fade in
        } else if (!fadeOutReady) {
          meshOp = 1;                                   // hold — wait for texture + pre-landing window
        } else {
          if (fadeOutStart < 0) {
            fadeOutStart = now;
            // Align sphere to destination before it becomes visible through the fading mesh.
            if (this.sphere) this.sphere.rotation.y = this.source.getSphereYaw(destNodeId);
          }
          meshOp = 1 - Math.min(1, (now - fadeOutStart) / FADE); // fade out
        }
        this._setMeshOpacity(meshOp);

        const done = flightDone && textureLoaded
          && fadeOutStart >= 0 && (now - fadeOutStart) >= FADE;

        if (!done && this.active_) {
          this.transitionRaf = requestAnimationFrame(tick);
        } else {
          // Clean up ghost and mesh, hand control back to navigate().
          if (this.ghostSphere) { this.scene.remove(this.ghostSphere); this.ghostSphere = null; }
          if (this.transitionMaterial) { this.transitionMaterial.dispose(); this.transitionMaterial = null; }
          this.transitionRaf = 0;
          this._restoreMeshStates();
          this.planRoot.visible = false;
          this.meshFlightResolve = null;
          resolve();
        }
      };

      this.transitionRaf = requestAnimationFrame(tick);
    });
  }

  private _saveMeshStates(): MeshState[] {
    const states: MeshState[] = [];
    this.planRoot.traverse(obj => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      states.push({
        mesh,
        materials: mats.map(m => ({
          mat: m,
          transparent: (m as THREE.MeshStandardMaterial).transparent,
          opacity:      (m as THREE.MeshStandardMaterial).opacity,
          depthWrite:   (m as THREE.MeshStandardMaterial).depthWrite,
        })),
      });
    });
    return states;
  }

  private _setMeshOpacity(opacity: number): void {
    const applyMat = (m: THREE.Material): void => {
      const ms = m as THREE.MeshStandardMaterial;
      ms.transparent = opacity < 1;
      ms.opacity     = opacity;
      ms.depthWrite  = opacity >= 1;
      ms.needsUpdate = true;
    };
    for (const state of this.savedMeshStates) {
      if (Array.isArray(state.mesh.material)) state.mesh.material.forEach(applyMat);
      else applyMat(state.mesh.material);
    }
  }

  private _restoreMeshStates(): void {
    for (const { mesh, materials } of this.savedMeshStates) {
      const restore = (m: THREE.Material, s: MeshMatState): void => {
        const ms = m as THREE.MeshStandardMaterial;
        ms.transparent = s.transparent;
        ms.opacity     = s.opacity;
        ms.depthWrite  = s.depthWrite;
        ms.needsUpdate = true;
      };
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m, i) => restore(m, materials[i]));
      } else {
        restore(mesh.material, materials[0]);
      }
    }
    this.savedMeshStates = [];
  }

  private _applyBlend(opacity: number): void {
    const set = (m: THREE.Material): void => {
      const ms = m as THREE.MeshStandardMaterial;
      ms.transparent = true;
      ms.opacity     = opacity;
      ms.depthWrite  = false;
      ms.needsUpdate = true;
    };
    for (const { mesh } of this.blendStates) {
      if (Array.isArray(mesh.material)) mesh.material.forEach(set);
      else set(mesh.material);
    }
  }

  private _clearBlend(): void {
    for (const { mesh, materials } of this.blendStates) {
      const restore = (m: THREE.Material, s: MeshMatState): void => {
        const ms = m as THREE.MeshStandardMaterial;
        ms.transparent = s.transparent;
        ms.opacity     = s.opacity;
        ms.depthWrite  = s.depthWrite;
        ms.needsUpdate = true;
      };
      if (Array.isArray(mesh.material)) mesh.material.forEach((m, i) => restore(m, materials[i]));
      else restore(mesh.material, materials[0]);
    }
    this.blendStates = [];
    this.planRoot.visible = false;
  }

  private _cancelTransition(): void {
    if (this.transitionRaf) {
      cancelAnimationFrame(this.transitionRaf);
      this.transitionRaf = 0;
    }
    if (this.ghostSphere) {
      this.scene.remove(this.ghostSphere);
      this.ghostSphere = null;
    }
    if (this.transitionMaterial) {
      // Don't dispose .map — it's a texture owned by the source's cache.
      this.transitionMaterial.dispose();
      this.transitionMaterial = null;
    }
    this._restoreMeshStates();
    // planRoot is always false during panorama; exit() will set it true when needed.
    this.planRoot.visible = false;
    const resolve = this.meshFlightResolve;
    this.meshFlightResolve = null;
    resolve?.();
  }

  // ── In-scene hotspots (clickable markers inside the panorama) ─────────────

  private _ensureHotspotGroup(): void {
    if (!this.hotspotGroup) {
      this.hotspotGroup = new THREE.Group();
      this.hotspotGroup.renderOrder = 100;
    }
  }

  private _disposeHotspotChildren(): void {
    if (!this.hotspotGroup) return;
    for (const child of [...this.hotspotGroup.children]) {
      this.hotspotGroup.remove(child);
      child.traverse(o => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        const dispose = (m: THREE.Material): void => {
          const tex = (m as { map?: THREE.Texture }).map;
          tex?.dispose();
          m.dispose();
        };
        if (Array.isArray(mat)) mat.forEach(dispose);
        else if (mat) dispose(mat);
      });
    }
  }

  /** Build a flat ground-level hotspot (ring + chevron pointing toward target). */
  private _buildHotspot(targetId: number, dirX: number, dirZ: number): THREE.Object3D {
    const grp = new THREE.Group();
    grp.userData.isHotspot = true;
    grp.userData.nodeId = targetId;

    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x4cc9f0,
      transparent: true,
      opacity: 0.55,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.23, 0.43, 32), ringMat);
    ring.rotation.x = -Math.PI / 2;
    grp.add(ring);

    // Chevron: shape's +Y becomes world -Z after rotating x by -PI/2 (i.e. tip points -Z locally).
    const s = new THREE.Shape();
    s.moveTo(0, 0.15);
    s.lineTo(0.15, -0.083);
    s.lineTo(0, -0.017);
    s.lineTo(-0.15, -0.083);
    s.lineTo(0, 0.15);
    const arrowMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.92,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const arrow = new THREE.Mesh(new THREE.ShapeGeometry(s), arrowMat);
    arrow.rotation.x = -Math.PI / 2;
    arrow.position.y = 0.005;  // small lift to avoid z-fight with ring
    grp.add(arrow);

    // Aim local -Z (chevron tip) toward (dirX, dirZ): rotate around Y by atan2(-dirX, -dirZ).
    grp.rotation.y = Math.atan2(-dirX, -dirZ);
    return grp;
  }

  private _updateHotspots(nodeId: number): void {
    if (!this.hotspotGroup) return;
    this._setHotspotHover(null);
    this._disposeHotspotChildren();

    const cp = this._pos(nodeId);
    if (!cp) return;

    for (const nid of this.source.getNeighbors(nodeId)) {
      const np = this._pos(nid);
      if (!np) continue;
      const dx = np.x - cp.x;
      const dz = np.z - cp.z;
      const len = Math.hypot(dx, dz);
      if (len < 1e-3) continue;
      const ux = dx / len;
      const uz = dz / len;
      const h = this._buildHotspot(nid, ux, uz);
      // Place ring at the actual floor of the target node so it always appears
      // below the blue sphere marker (which is 0.5 m above floor = CAMERA_HEIGHT - 1.05).
      h.position.set(np.x, np.y - CAMERA_HEIGHT, np.z);
      this.hotspotGroup.add(h);
    }
  }

  private _pickHotspot(e: PointerEvent): THREE.Object3D | null {
    if (!this.hotspotGroup || this.hotspotGroup.children.length === 0) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1),
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    const hits = ray.intersectObjects(this.hotspotGroup.children, true);
    if (hits.length === 0) return null;
    let obj: THREE.Object3D | null = hits[0].object;
    while (obj && !obj.userData.isHotspot) obj = obj.parent;
    return obj;
  }

  private _setHotspotHover(obj: THREE.Object3D | null): void {
    if (this.hoveredHotspot === obj) return;
    if (this.hoveredHotspot) this.hoveredHotspot.scale.setScalar(1.0);
    this.hoveredHotspot = obj;
    if (obj) obj.scale.setScalar(1.25);
    if (!this.dragging) {
      this.renderer.domElement.style.cursor = obj ? "pointer" : "grab";
    }
  }

  private _down = (e: PointerEvent): void => {
    if (!this.active_ || e.button !== 0) return;
    e.stopPropagation();
    this.dragging = true;
    this.dragMoved = false;
    this.downX = this.lastX = e.clientX;
    this.downY = this.lastY = e.clientY;
    this.renderer.domElement.setPointerCapture(e.pointerId);
    this.renderer.domElement.style.cursor = "grabbing";
  };

  private _move = (e: PointerEvent): void => {
    if (!this.active_) return;
    if (this.dragging) {
      e.stopPropagation();
      if (!this.dragMoved && Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > 4) {
        this.dragMoved = true;
      }
      this.yaw += (e.clientX - this.lastX) * DRAG_SENSITIVITY;
      this.pitch += (e.clientY - this.lastY) * DRAG_SENSITIVITY;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      this._applyCamera();
    } else {
      this._setHotspotHover(this._pickHotspot(e));
    }
  };

  private _up = (e: PointerEvent): void => {
    if (!this.active_) return;
    e.stopPropagation();
    const wasClick = this.dragging && !this.dragMoved;
    this.dragging = false;
    try { this.renderer.domElement.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    if (wasClick) {
      const target = this._pickHotspot(e);
      if (target) {
        const nid = target.userData.nodeId as number | undefined;
        if (typeof nid === "number") void this.navigate(nid);
      }
    }
    this.renderer.domElement.style.cursor = this.hoveredHotspot ? "pointer" : "grab";
  };

  private _wheel = (e: WheelEvent): void => {
    if (!this.active_ || this.loading) return;
    e.preventDefault();
    const fwd = e.deltaY < 0 ? 1 : -1;
    this.navigateInDirection(Math.sin(this.yaw) * fwd, Math.cos(this.yaw) * fwd);
  };

  private _key = (e: KeyboardEvent): void => {
    if (!this.active_) return;
    if (e.key === "Escape") { e.stopPropagation(); this.exit(); return; }
    const dirMap: Record<string, [number, number]> = {
      ArrowRight: [1, 0], d: [1, 0], D: [1, 0],
      ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0],
      ArrowUp: [0, -1], w: [0, -1], W: [0, -1],
      ArrowDown: [0, 1], s: [0, 1], S: [0, 1],
    };
    const dir = dirMap[e.key];
    if (dir) { e.stopPropagation(); this.navigateInDirection(dir[0], dir[1]); }
  };
}
